import { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { GetUtilityData } from "../../../services/utilityService";
import { subscribeUtilityRefresh } from "../../../utils/utilityRefreshEvent";
import { subscribeTenantChange } from "../../../utils/tenantChangeEvent";

interface TrendUtilityDataItem {
  Year: number;
  Month: number;
  MonthName: string;
  Electric: number;
  Water: number;
  Gas: number;
}

interface TrendUtilityResponse {
  utilityDatas: TrendUtilityDataItem[];
  ElectricityAmountThisMonth: number;
  ElectricityAmountLastMonth: number;
  ElectricityPercentThisMonth: number;
  ElectricityPercentLastMonth: number;
  GasAmountThisMonth: number;
  GasAmountLastMonth: number;
  GasPercentThisMonth: number;
  GasPercentLastMonth: number;
  WaterAmountThisMonth: number;
  WaterAmountLastMonth: number;
  WaterPercentThisMonth: number;
  WaterPercentLastMonth: number;
  LastUpdate: string;
  Code: number;
  Message: string;
}

const UTILITY_FALLBACK: TrendUtilityResponse = {
  utilityDatas: [],
  ElectricityAmountThisMonth: 0,
  ElectricityAmountLastMonth: 0,
  ElectricityPercentThisMonth: 0,
  ElectricityPercentLastMonth: 0,
  GasAmountThisMonth: 0,
  GasAmountLastMonth: 0,
  GasPercentThisMonth: 0,
  GasPercentLastMonth: 0,
  WaterAmountThisMonth: 0,
  WaterAmountLastMonth: 0,
  WaterPercentThisMonth: 0,
  WaterPercentLastMonth: 0,
  LastUpdate: "",
  Code: -1,
  Message: "",
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-300 mb-1">
          {label}
        </div>
        {payload.map((p: any, i: number) => (
          <div key={i} className="text-sm text-gray-700 dark:text-gray-100">
            <span className="font-medium">{p.name}</span>:{" "}
            <span className="ml-1">
              Rp
              {Number(p.value).toLocaleString("id-ID", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};


export default function TrendUtilitySection() {
  const [activeTab, setActiveTab] = useState<"Electricity" | "Gas" | "Water">(
    "Electricity"
  );
  const [loading, setLoading] = useState(true);
  const [utilityData, setUtilityData] = useState<TrendUtilityResponse | null>(null);

  useEffect(() => {
    const fetchUtilityData = async () => {
      try {
        const data = await GetUtilityData(false);
        if (data) {
          setUtilityData(
            data.Code !== 0
              ? { ...UTILITY_FALLBACK, LastUpdate: data.LastUpdate }
              : (data as unknown as TrendUtilityResponse)
          );
        }
      } catch (err) {
        console.log("Failed to load utility data", err);
        setUtilityData(UTILITY_FALLBACK);
      } finally {
        setLoading(false);
      }
    };

    fetchUtilityData();
  }, []);

  useEffect(() => {
    const handleRefresh = async () => {
      try {
        const data = await GetUtilityData(true);
        if (data) {
          setUtilityData(
            data.Code !== 0
              ? { ...UTILITY_FALLBACK, LastUpdate: data.LastUpdate }
              : (data as unknown as TrendUtilityResponse)
          );
        }
      } catch (err) {
        console.log("Failed to refresh utility data", err);
      }
    };

    const unsubscribe = subscribeUtilityRefresh(handleRefresh);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleTenantChange = async () => {
      setLoading(true);
      try {
        const data = await GetUtilityData(false); // isreload=0
        if (data) {
          setUtilityData(
            data.Code !== 0
              ? { ...UTILITY_FALLBACK, LastUpdate: data.LastUpdate }
              : (data as unknown as TrendUtilityResponse)
          );
        }
      } catch (err) {
        console.log("Failed to fetch utility after tenant change", err);
        setUtilityData(UTILITY_FALLBACK);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = subscribeTenantChange(handleTenantChange);
    return unsubscribe;
  }, []);

  const prepareChartData = () => {
    if (!utilityData) return [];

    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    const utilityKey = activeTab === "Electricity" ? "Electric" : activeTab;
    const dataByYear: { [year: number]: { [month: string]: number } } = {};

    utilityData.utilityDatas.forEach((item: TrendUtilityDataItem) => {
      if (!dataByYear[item.Year]) {
        dataByYear[item.Year] = {};
      }
      dataByYear[item.Year][item.MonthName] = item[
        utilityKey as keyof TrendUtilityDataItem
      ] as number;
    });

    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    return months.map((month) => ({
      month,
      [`${lastYear}`]: dataByYear[lastYear]?.[month] || 0,
      [`${currentYear}`]: dataByYear[currentYear]?.[month] || 0,
    }));
  };

  const getCurrentMetrics = () => {
    if (!utilityData) {
      return {
        thisMonth: 0,
        lastMonth: 0,
        percentThisMonth: 0,
        percentLastMonth: 0,
      };
    }

    const prefix = activeTab === "Electricity" ? "Electricity" : activeTab;

    return {
      thisMonth: utilityData[
        `${prefix}AmountThisMonth` as keyof TrendUtilityResponse
      ] as number,
      lastMonth: utilityData[
        `${prefix}AmountLastMonth` as keyof TrendUtilityResponse
      ] as number,
      percentThisMonth: utilityData[
        `${prefix}PercentThisMonth` as keyof TrendUtilityResponse
      ] as number,
      percentLastMonth: utilityData[
        `${prefix}PercentLastMonth` as keyof TrendUtilityResponse
      ] as number,
    };
  };

  const formatCurrency = (value: number) => {
    return `Rp${value.toLocaleString("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatPercent = (value: number) => {
    const absValue = Math.abs(value);
    const truncated = Math.floor(absValue * 10) / 10;
    return `${truncated}%`;
  };

  const formatLastUpdate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const revenueData = prepareChartData();
  const metrics = getCurrentMetrics();
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  if (loading) {
    return (
      <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-sm border border-gray-100 dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center justify-center h-64">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Loading...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-sm border border-gray-100 dark:bg-gray-900 dark:border-gray-700">
      <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
          Trend Utility
        </h2>

        <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 rounded-full px-2 py-1 border dark:border-gray-700 w-full sm:w-auto overflow-x-auto">
          {(["Electricity", "Gas", "Water"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${activeTab === tab
                ? "bg-blue-600 text-white"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              aria-pressed={activeTab === tab}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-600 flex-shrink-0" />
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
              This Month
            </div>
          </div>
          <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 break-all">
            {formatCurrency(metrics.thisMonth)}
          </div>
          <div
            className={`text-xs sm:text-sm font-medium ${metrics.percentThisMonth < 0 ? "text-red-500" : "text-emerald-600"
              }`}
          >
            {metrics.percentThisMonth < 0 ? "↓" : "↑"}{" "}
            {formatPercent(metrics.percentThisMonth)} this month
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-400 flex-shrink-0" />
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Last Month
            </div>
          </div>
          <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 break-all">
            {formatCurrency(metrics.lastMonth)}
          </div>
          <div
            className={`text-xs sm:text-sm font-medium ${metrics.percentLastMonth < 0 ? "text-red-500" : "text-emerald-600"
              }`}
          >
            {metrics.percentLastMonth < 0 ? "↓" : "↑"}{" "}
            {formatPercent(metrics.percentLastMonth)} last month
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-8">
        <div className="h-48 sm:h-64 rounded-xl bg-gradient-to-b from-gray-50 to-white ring-1 ring-inset ring-gray-100 p-2 sm:p-4 dark:from-gray-800 dark:to-gray-900 dark:ring-gray-700">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={revenueData}
              margin={{ top: 6, right: 8, left: -24, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gradBlueMain" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                stroke="#E5E7EB"
                className="dark:stroke-gray-700"
              />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#6B7280" }}
                padding={{ left: 4, right: 4 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#6B7280" }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(0)}M`}
              />
              <Tooltip content={<CustomTooltip />} />

              <Area
                type="monotone"
                dataKey={`${lastYear}`}
                name={`${lastYear}`}
                stroke="#9CA3AF"
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="transparent"
                dot={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey={`${currentYear}`}
                name={`${currentYear}`}
                stroke="#2563EB"
                strokeWidth={2}
                fill="url(#gradBlueMain)"
                dot={{ fill: "#2563EB", strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, fill: "#2563EB" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 sm:mt-4 flex flex-col items-center gap-2">
          <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
            <div className="inline-flex items-center gap-2 font-medium text-gray-700 dark:text-white">
              <span className="inline-block h-0.5 w-5 sm:w-6 bg-blue-600 rounded-full" />
              {currentYear}
            </div>
            <div className="inline-flex items-center gap-2 text-gray-500 font-medium dark:text-gray-300">
              <span
                className="inline-block h-0.5 w-5 sm:w-6 rounded-full"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, #9CA3AF 0px, #9CA3AF 3px, transparent 3px, transparent 6px)",
                }}
              />
              {lastYear}
            </div>
          </div>

          {utilityData?.LastUpdate && (
            <div className="text-xs text-gray-500 text-center">
              Last update: {formatLastUpdate(utilityData.LastUpdate)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}